import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useSearchParams } from 'react-router-dom';

import { axiosPrivate } from 'app/utils';

export type OnboardingJourney = 'pharmacy-select' | 'marketplace' | 'delivery-collection';
export type LandingType = 'in-app' | 'external';

export interface PlusWebsite {
  id: string;
  domain: string | null;
  alternativeDomain: string;
  isDeployed: boolean;
  hasUnpublishedChanges: boolean;
  landingType?: LandingType | null;
  externalHomepageUrl?: string | null;
  onboardingJourney?: OnboardingJourney | null;
  googleTagManagerId?: string | null;
  googleMeasurementId?: string | null;
}

// Older websites predate landingType; an external homepage URL is what made
// them external in the first place.
export function resolveLandingType(website: PlusWebsite | undefined): LandingType {
  if (website?.landingType) return website.landingType;
  return website?.externalHomepageUrl ? 'external' : 'in-app';
}

export interface PlusWebsitesResponse {
  plusWebsites: PlusWebsite[];
  pagination: {
    total: number;
    currentPage: number;
  };
}

// Every organisation's list sits under this prefix, which is what invalidation matches on.
export const plusWebsitesQueryKey = ['plus-websites'];

// The one list an organisation reads, shared with the imperative fetch website creation needs.
export function plusWebsitesQuery(organisationId: string) {
  return {
    queryKey: [...plusWebsitesQueryKey, organisationId],
    queryFn: () => getPlusWebsites(organisationId),
  };
}

// Editing preview content leaves the published site out of date, but the list
// endpoint that carries hasUnpublishedChanges is only refetched on remount — so
// the Publish button would stay disabled until the user navigated away and back.
export function markWebsiteAsChanged(queryClient: QueryClient, websiteId: string): void {
  queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>(
    { queryKey: plusWebsitesQueryKey },
    (old) =>
      old
        ? {
            ...old,
            data: {
              ...old.data,
              plusWebsites: old.data.plusWebsites.map((website) =>
                website.id === websiteId ? { ...website, hasUnpublishedChanges: true } : website
              ),
            },
          }
        : old
  );
}

function getPlusWebsites(organisationId: string) {
  return axiosPrivate.get<PlusWebsitesResponse>('/v3/pharmacist/plus-websites', {
    params: { organisationId },
  });
}

export interface UsePlusWebsitesResult {
  plusWebsites: PlusWebsite[];
  isLoading: boolean;
  isError: boolean;
}

export default function usePlusWebsites(): UsePlusWebsitesResult {
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';

  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useQuery({
    ...plusWebsitesQuery(organisationId),
    enabled: !!organisationId,
    meta: { errorMessage: 'Failed to load Plus Websites' },
  });

  const plusWebsites = data?.data.plusWebsites ?? [];

  return {
    plusWebsites,
    isLoading,
    isError,
  };
}
